/**
 * Above the Belegjournal: search, status and a "Filters" sheet (invoice date, partner, amount,
 * category, payment, VAT treatment), with every active filter as a removable chip underneath.
 * Modelled on blanqo's invoice list.
 */
import { Check, ChevronDown, Search, SlidersHorizontal, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { DatePicker } from "@/components/shared/date-picker";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { usePartnerNames } from "@/hooks/use-documents";
import { useIsMobile } from "@/hooks/use-mobile";
import { DOCUMENT_STATUSES, type DocumentStatus } from "@/lib/api/documents";
import { TAX_TREATMENTS } from "@/lib/at/vat";
import {
  DATE_PRESETS,
  EMPTY_FILTERS,
  matchingPreset,
  OPEN_STATUSES,
  parseDay,
  presetRange,
  sheetFilterCount,
  type DatePreset,
  type DocumentFilters,
  type PaymentStatus,
} from "@/lib/documents/document-list";
import { formatDate, formatMoney } from "@/lib/utils/format";
import type { CategoriesResponse } from "@/lib/platform/pb-types";
import { cn } from "@/lib/utils/cn";

const SEARCH_DELAY = 300;
const ANY = "any";
const CUSTOM = "custom";
const PAYMENTS: PaymentStatus[] = ["open", "paid", "partial", "not_applicable"];

type ToolbarProps = {
  workspaceId: string | undefined;
  filters: DocumentFilters;
  categories: CategoriesResponse[];
  onChange: (changes: Partial<DocumentFilters>) => void;
};

export function DocumentToolbar({ workspaceId, filters, categories, onChange }: ToolbarProps) {
  const { t } = useTranslation();
  const [sheetOpen, setSheetOpen] = useState(false);
  // A fresh draft each time the sheet opens.
  const [sheetKey, setSheetKey] = useState(0);
  const count = sheetFilterCount(filters);

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <SearchBox value={filters.search} onChange={(search) => onChange({ search })} />
        <StatusMenu value={filters.statuses} onChange={(statuses) => onChange({ statuses })} />
        <Button
          variant="outline"
          className="bg-card"
          onClick={() => {
            setSheetKey((key) => key + 1);
            setSheetOpen(true);
          }}
          data-testid="open-filters"
        >
          <SlidersHorizontal />
          {t("documents.filters.button")}
          {count > 0 && (
            <Badge variant="secondary" className="ml-0.5 rounded-full px-1.5 num">
              {count}
            </Badge>
          )}
        </Button>
      </div>
      <FilterChips filters={filters} categories={categories} onChange={onChange} />
      <FilterSheet
        key={sheetKey}
        workspaceId={workspaceId}
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        filters={filters}
        categories={categories}
        onApply={(changes) => {
          onChange(changes);
          setSheetOpen(false);
        }}
      />
    </div>
  );
}

/** The list follows once typing pauses; Enter or leaving the box applies at once. */
function SearchBox({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  const { t } = useTranslation();
  const [text, setText] = useState(value);
  // A change from elsewhere ("Clear all") wins over what was typed.
  const [synced, setSynced] = useState(value);
  if (value !== synced) {
    setSynced(value);
    setText(value);
  }
  const latest = useRef(onChange);
  useEffect(() => {
    latest.current = onChange;
  });
  useEffect(() => {
    if (text.trim() === value.trim()) return;
    const timer = setTimeout(() => latest.current(text), SEARCH_DELAY);
    return () => clearTimeout(timer);
  }, [text, value]);

  return (
    <div className="relative w-full sm:w-72">
      <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
      <Input
        type="search"
        value={text}
        onChange={(event) => setText(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Enter") onChange(text);
        }}
        // Leaving the box applies at once: a click on a row right after typing must not be
        // followed by the delayed search, which would navigate back to the list.
        onBlur={() => text.trim() !== value.trim() && onChange(text)}
        placeholder={t("documents.searchPlaceholder")}
        aria-label={t("common.search")}
        className="pr-9 pl-9 [&::-webkit-search-cancel-button]:hidden"
        data-testid="search"
      />
      {text && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="absolute top-1/2 right-1 size-7 -translate-y-1/2"
          onClick={() => {
            setText("");
            onChange("");
          }}
          aria-label={t("documents.filters.clearSearch")}
        >
          <X />
        </Button>
      )}
    </div>
  );
}

const isOpen = (statuses: DocumentStatus[]) =>
  statuses.length === OPEN_STATUSES.length && OPEN_STATUSES.every((s) => statuses.includes(s));

function StatusMenu({
  value,
  onChange,
}: {
  value: DocumentStatus[];
  onChange: (value: DocumentStatus[]) => void;
}) {
  const { t } = useTranslation();
  const label =
    value.length === 0
      ? t("documents.filters.allStatuses")
      : isOpen(value)
        ? t("documents.statusOpen")
        : value.length === 1
          ? t(`status.${value[0]!}`)
          : t("documents.filters.statusCount", { count: value.length });
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          className={cn("justify-between bg-card sm:w-48", value.length && "border-foreground/30")}
          aria-label={t("documents.status")}
          data-testid="filter-status"
        >
          <span className="truncate">{label}</span>
          <ChevronDown className="opacity-50" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-52">
        <DropdownMenuItem onSelect={() => onChange(OPEN_STATUSES)} data-testid="status-open">
          {t("documents.statusOpen")}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        {DOCUMENT_STATUSES.map((status) => (
          <DropdownMenuCheckboxItem
            key={status}
            checked={value.includes(status)}
            // Stays open, to pick several.
            onSelect={(event) => event.preventDefault()}
            onCheckedChange={(on) =>
              onChange(
                on
                  ? DOCUMENT_STATUSES.filter((s) => s === status || value.includes(s))
                  : value.filter((s) => s !== status),
              )
            }
            data-testid={`status-option-${status}`}
          >
            {t(`status.${status}`)}
          </DropdownMenuCheckboxItem>
        ))}
        {value.length > 0 && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => onChange([])}>
              {t("documents.filters.allStatuses")}
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

const day = (value: string) => formatDate(parseDay(value));

function FilterChips({
  filters,
  categories,
  onChange,
}: {
  filters: DocumentFilters;
  categories: CategoriesResponse[];
  onChange: (changes: Partial<DocumentFilters>) => void;
}) {
  const { t } = useTranslation();
  const chips: { key: string; label: string; remove: () => void }[] = [];

  if (filters.from || filters.to) {
    const label = t("documents.filters.period");
    chips.push({
      key: "date",
      label:
        filters.from && filters.to
          ? t("documents.filters.chip.range", {
              label,
              from: day(filters.from),
              to: day(filters.to),
            })
          : filters.from
            ? t("documents.filters.chip.from", { label, value: day(filters.from) })
            : t("documents.filters.chip.until", { label, value: day(filters.to) }),
      remove: () => onChange({ from: "", to: "" }),
    });
  }
  for (const name of filters.partners) {
    chips.push({
      key: `partner-${name}`,
      label: name,
      remove: () => onChange({ partners: filters.partners.filter((p) => p !== name) }),
    });
  }
  if (filters.min || filters.max) {
    const label = t("documents.gross");
    const money = (value: string) => formatMoney(Number(value));
    chips.push({
      key: "amount",
      label:
        filters.min && filters.max
          ? t("documents.filters.chip.range", {
              label,
              from: money(filters.min),
              to: money(filters.max),
            })
          : filters.min
            ? t("documents.filters.chip.from", { label, value: money(filters.min) })
            : t("documents.filters.chip.until", { label, value: money(filters.max) }),
      remove: () => onChange({ min: "", max: "" }),
    });
  }
  if (filters.category) {
    chips.push({
      key: "category",
      label: categories.find((c) => c.id === filters.category)?.name ?? t("documents.category"),
      remove: () => onChange({ category: "" }),
    });
  }
  if (filters.payment) {
    chips.push({
      key: "payment",
      label: `${t("documents.filters.payment")}: ${t(`paymentStatus.${filters.payment}`)}`,
      remove: () => onChange({ payment: "" }),
    });
  }
  if (filters.treatment) {
    chips.push({
      key: "treatment",
      label: t(`taxTreatment.${filters.treatment}`),
      remove: () => onChange({ treatment: "" }),
    });
  }
  // Search and status show in their own controls. Only the sheet's filters get chips, so typing a
  // search does not push the list down under the pointer.
  if (!chips.length) return null;

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="filter-chips">
      {chips.map((chip) => (
        <span
          key={chip.key}
          className="inline-flex h-7 max-w-full items-center gap-1 rounded-full border bg-card pr-1 pl-3 text-xs"
          data-testid="filter-chip"
        >
          <span className="truncate">{chip.label}</span>
          <button
            type="button"
            onClick={chip.remove}
            className="inline-flex size-5 shrink-0 items-center justify-center rounded-full text-muted-foreground hover:bg-accent hover:text-foreground"
            aria-label={t("documents.filters.remove", { label: chip.label })}
          >
            <X className="size-3" />
          </button>
        </span>
      ))}
      <Button
        variant="link"
        size="sm"
        className="h-7 px-1 text-xs"
        onClick={() => {
          const { kind: _kind, ...cleared } = EMPTY_FILTERS;
          onChange(cleared);
        }}
        data-testid="clear-filters"
      >
        {t("documents.filters.clearAll")}
      </Button>
    </div>
  );
}

type SheetValues = Pick<
  DocumentFilters,
  "from" | "to" | "partners" | "min" | "max" | "category" | "payment" | "treatment"
>;

/** The sheet edits a draft; "Show results" applies it. */
function FilterSheet({
  workspaceId,
  open,
  onOpenChange,
  filters,
  categories,
  onApply,
}: {
  workspaceId: string | undefined;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  filters: DocumentFilters;
  categories: CategoriesResponse[];
  onApply: (changes: SheetValues) => void;
}) {
  const { t } = useTranslation();
  const mobile = useIsMobile();
  const [draft, setDraft] = useState<SheetValues>(() => ({
    from: filters.from,
    to: filters.to,
    partners: filters.partners,
    min: filters.min,
    max: filters.max,
    category: filters.category,
    payment: filters.payment,
    treatment: filters.treatment,
  }));
  const set = (changes: Partial<SheetValues>) =>
    setDraft((current) => ({ ...current, ...changes }));
  const [period, setPeriod] = useState<string>(
    () => matchingPreset(draft.from, draft.to) || (draft.from || draft.to ? CUSTOM : ANY),
  );
  const amount = (value: string) => Number(value.replace(",", "."));
  const amountInvalid =
    (draft.min !== "" && !Number.isFinite(amount(draft.min))) ||
    (draft.max !== "" && !Number.isFinite(amount(draft.max))) ||
    (draft.min !== "" && draft.max !== "" && amount(draft.min) > amount(draft.max));

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side={mobile ? "bottom" : "right"}
        className={cn(
          "flex flex-col gap-0 p-0",
          mobile ? "max-h-[90dvh] rounded-t-xl" : "w-full sm:max-w-md",
        )}
        data-testid="filter-sheet"
      >
        <SheetHeader className="border-b p-4 text-left">
          <SheetTitle>{t("documents.filters.title")}</SheetTitle>
          <SheetDescription>{t("documents.filters.hint")}</SheetDescription>
        </SheetHeader>

        <div className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto p-4">
          <section className="flex flex-col gap-2">
            <Label htmlFor="filter-period">{t("documents.filters.period")}</Label>
            <Select
              value={period}
              onValueChange={(value) => {
                setPeriod(value);
                if (value === ANY) set({ from: "", to: "" });
                else if (value !== CUSTOM) set(presetRange(value as DatePreset));
              }}
            >
              <SelectTrigger id="filter-period" data-testid="filter-period">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ANY}>{t("documents.filters.anyTime")}</SelectItem>
                {DATE_PRESETS.map((value) => (
                  <SelectItem key={value} value={value}>
                    {t(`documents.filters.presets.${value}`)}
                  </SelectItem>
                ))}
                <SelectItem value={CUSTOM}>{t("documents.filters.custom")}</SelectItem>
              </SelectContent>
            </Select>
            {period !== ANY && (
              <div className="grid grid-cols-2 gap-2">
                <div className="flex flex-col gap-1">
                  <span className="text-xs text-muted-foreground">{t("common.from")}</span>
                  <DatePicker
                    value={draft.from}
                    aria-label={t("common.from")}
                    data-testid="filter-from"
                    onChange={(from) => {
                      set({ from });
                      setPeriod(matchingPreset(from, draft.to) || CUSTOM);
                    }}
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <span className="text-xs text-muted-foreground">{t("common.to")}</span>
                  <DatePicker
                    value={draft.to}
                    aria-label={t("common.to")}
                    data-testid="filter-to"
                    onChange={(to) => {
                      set({ to });
                      setPeriod(matchingPreset(draft.from, to) || CUSTOM);
                    }}
                  />
                </div>
              </div>
            )}
          </section>

          <section className="flex flex-col gap-2">
            <Label>{t("documents.filters.partner")}</Label>
            <PartnerPicker
              workspaceId={workspaceId}
              enabled={open}
              value={draft.partners}
              onChange={(partners) => set({ partners })}
            />
          </section>

          <section className="flex flex-col gap-2">
            <Label>{t("documents.filters.amount")}</Label>
            <div className="grid grid-cols-2 gap-2">
              <Input
                inputMode="decimal"
                className="num"
                value={draft.min}
                onChange={(event) => set({ min: event.target.value })}
                placeholder={t("documents.filters.min")}
                aria-label={t("documents.filters.minLabel")}
                aria-invalid={amountInvalid || undefined}
                data-testid="filter-min"
              />
              <Input
                inputMode="decimal"
                className="num"
                value={draft.max}
                onChange={(event) => set({ max: event.target.value })}
                placeholder={t("documents.filters.max")}
                aria-label={t("documents.filters.maxLabel")}
                aria-invalid={amountInvalid || undefined}
                data-testid="filter-max"
              />
            </div>
            {amountInvalid && (
              <p className="text-sm text-destructive" role="alert">
                {t("documents.filters.amountInvalid")}
              </p>
            )}
          </section>

          <section className="flex flex-col gap-2">
            <Label htmlFor="filter-category">{t("documents.category")}</Label>
            <Select
              value={draft.category || ANY}
              onValueChange={(value) => set({ category: value === ANY ? "" : value })}
            >
              <SelectTrigger id="filter-category" data-testid="filter-category">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ANY}>{t("documents.filters.any")}</SelectItem>
                {categories.map((category) => (
                  <SelectItem key={category.id} value={category.id}>
                    {category.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </section>

          <section className="flex flex-col gap-2">
            <Label htmlFor="filter-payment">{t("documents.filters.payment")}</Label>
            <Select
              value={draft.payment || ANY}
              onValueChange={(value) =>
                set({ payment: value === ANY ? "" : (value as PaymentStatus) })
              }
            >
              <SelectTrigger id="filter-payment" data-testid="filter-payment">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ANY}>{t("documents.filters.any")}</SelectItem>
                {PAYMENTS.map((payment) => (
                  <SelectItem key={payment} value={payment}>
                    {t(`paymentStatus.${payment}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </section>

          <section className="flex flex-col gap-2">
            <Label htmlFor="filter-treatment">{t("documents.filters.treatment")}</Label>
            <Select
              value={draft.treatment || ANY}
              onValueChange={(value) =>
                set({ treatment: value === ANY ? "" : (value as SheetValues["treatment"]) })
              }
            >
              <SelectTrigger id="filter-treatment" data-testid="filter-treatment">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ANY}>{t("documents.filters.any")}</SelectItem>
                {TAX_TREATMENTS.map((treatment) => (
                  <SelectItem key={treatment} value={treatment}>
                    {t(`taxTreatment.${treatment}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </section>
        </div>

        <SheetFooter className="flex-row justify-between gap-2 border-t p-4 sm:justify-between">
          <Button
            variant="ghost"
            onClick={() => {
              setDraft({
                from: "",
                to: "",
                partners: [],
                min: "",
                max: "",
                category: "",
                payment: "",
                treatment: "",
              });
              setPeriod(ANY);
            }}
            data-testid="reset-filters"
          >
            {t("documents.filters.reset")}
          </Button>
          <Button
            onClick={() =>
              onApply({
                ...draft,
                // Stored the way the URL holds amounts: "12.5", not "12,50".
                min: draft.min.trim() ? String(amount(draft.min)) : "",
                max: draft.max.trim() ? String(amount(draft.max)) : "",
              })
            }
            disabled={amountInvalid}
            data-testid="apply-filters"
          >
            {t("documents.filters.apply")}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

/** Every name on the business's documents, searchable; several can be ticked. */
function PartnerPicker({
  workspaceId,
  enabled,
  value,
  onChange,
}: {
  workspaceId: string | undefined;
  enabled: boolean;
  value: string[];
  onChange: (value: string[]) => void;
}) {
  const { t } = useTranslation();
  const { data } = usePartnerNames(workspaceId, enabled);
  // Names ticked when the sheet opened come first; later ticks don't move rows around.
  const [pinned] = useState(value);
  const options = useMemo(() => {
    const all = [...new Set([...pinned, ...(data ?? [])])];
    return [...pinned, ...all.filter((name) => !pinned.includes(name))];
  }, [data, pinned]);
  const toggle = (name: string) =>
    onChange(value.includes(name) ? value.filter((p) => p !== name) : [...value, name]);

  return (
    <Command className="rounded-md border bg-card" data-testid="filter-partners">
      <CommandInput placeholder={t("documents.filters.partnerSearch")} />
      <CommandList className="max-h-48">
        <CommandEmpty>{t("documents.filters.noPartners")}</CommandEmpty>
        <CommandGroup>
          {options.map((name) => {
            const on = value.includes(name);
            return (
              <CommandItem
                key={name}
                value={name}
                onSelect={() => toggle(name)}
                data-checked={on}
                data-testid="partner-option"
              >
                <span
                  className={cn(
                    "flex size-4 shrink-0 items-center justify-center rounded-sm border",
                    on ? "border-primary bg-primary text-primary-foreground" : "opacity-60",
                  )}
                >
                  {on && <Check className="size-3" />}
                </span>
                <span className="truncate">{name}</span>
              </CommandItem>
            );
          })}
        </CommandGroup>
      </CommandList>
    </Command>
  );
}
